import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  Bell,
  Copy,
  DatabaseBackup,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Monitor,
  Moon,
  ShieldCheck,
  Smartphone,
  Sun,
  Trash2,
} from 'lucide-react';
import { formatDateBR, toDateSP } from '@lira/shared';
import type {
  ChangePasswordBody,
  CreateInviteBody,
  Invite,
  InvitesResponse,
  FinancialProfileContext,
  NotificationPreferences,
  PublicUser,
  SessionInfo,
  SessionsResponse,
  ThemePref,
  UpdateNotificationPreferencesBody,
} from '@lira/shared';
import { apiDelete, apiGet, apiPatch, apiPost } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { toast } from '@/stores/toast';
import { useAuthStore } from '@/stores/auth';
import { usePrivacyStore } from '@/stores/privacy';
import { useThemeStore } from '@/stores/theme';
import { ConfirmDialog, Skeleton, Toggle } from '@/components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { cn } from '@/lib/cn';
import { parseUserAgent } from './userAgent';
import { UsersAdmin } from './UsersAdmin';
import { ApiTokensCard } from './ApiTokensCard';
import { SupportAdmin } from './SupportAdmin';
import { OpenFinanceSettingsTab } from './OpenFinanceSettingsTab';

const ROLE_LABEL = { admin: 'Administrador', user: 'Usuário' } as const;

/** ISO datetime -> 'DD/MM/AAAA' (em America/Sao_Paulo). */
const dateBR = (iso: string) => formatDateBR(toDateSP(iso));

/** bytes -> rótulo humano curto (para o resultado do backup). */
function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
}

/** Card branco padrão (radius.lg + sombra leve do DESIGN). */
export function Card({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('rounded-[var(--radius-panel)] bg-surface p-6 shadow-card', className)}>{children}</div>;
}

// --- Convites (admin) ------------------------------------------------------

function inviteStatus(invite: Invite): { label: string; className: string } {
  if (invite.usedAt) {
    return { label: `Usado por ${invite.usedByName ?? '—'}`, className: 'text-muted' };
  }
  if (new Date(invite.expiresAt).getTime() < Date.now()) {
    return { label: 'Expirado', className: 'text-expense' };
  }
  return { label: 'Disponível', className: 'text-income' };
}

function CopyCodeButton({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard indisponível (http sem TLS etc.) */
    }
  };

  return (
    <button
      type="button"
      onClick={() => void handleCopy()}
      aria-label={copied ? 'Código copiado' : 'Copiar código'}
      title="Copiar código"
      className="grid size-8 place-items-center rounded-full text-muted transition-colors hover:bg-canvas hover:text-ink"
    >
      {copied ? <Check className="size-4 text-income" /> : <Copy className="size-4" />}
    </button>
  );
}

function InviteRow({
  invite,
  onDelete,
  deleting,
}: {
  invite: Invite;
  onDelete: (invite: Invite) => void;
  deleting: boolean;
}) {
  const status = inviteStatus(invite);

  return (
    <li className="flex items-center justify-between gap-3 py-3">
      <div className="min-w-0">
        <div className="flex items-center gap-1">
          <span className="truncate font-mono text-sm font-semibold text-ink">{invite.code}</span>
          <CopyCodeButton code={invite.code} />
        </div>
        <p className="truncate text-sm text-muted">
          {invite.email ?? '—'} · Expira em {dateBR(invite.expiresAt)}
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <span className={`text-xs font-medium ${status.className}`}>{status.label}</span>
        {!invite.usedAt && (
          <button
            type="button"
            onClick={() => onDelete(invite)}
            disabled={deleting}
            aria-label="Excluir convite"
            title="Excluir convite"
            className="grid size-8 place-items-center rounded-full text-muted transition-colors hover:bg-expense-weak hover:text-expense disabled:opacity-50"
          >
            <Trash2 className="size-4" />
          </button>
        )}
      </div>
    </li>
  );
}

function InvitesCard() {
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [days, setDays] = useState('7');
  const [confirmTarget, setConfirmTarget] = useState<Invite | null>(null);

  const invitesQuery = useQuery({
    queryKey: ['invites'],
    queryFn: () => apiGet<InvitesResponse>('/invites'),
  });

  const createInvite = useMutation({
    mutationFn: (body: CreateInviteBody) => apiPost<Invite>('/invites', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invites'] });
      setFormOpen(false);
      setEmail('');
      setDays('7');
    },
  });

  const deleteInvite = useMutation({
    mutationFn: (id: string) => apiDelete(`/invites/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invites'] });
      setConfirmTarget(null);
    },
  });

  const handleCreate = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (createInvite.isPending) return;
    const expiresInDays = Math.min(90, Math.max(1, Number.parseInt(days, 10) || 7));
    const trimmed = email.trim().toLowerCase();
    createInvite.mutate(trimmed ? { email: trimmed, expiresInDays } : { expiresInDays });
  };

  const invites = invitesQuery.data?.invites;

  return (
    <Card>
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-ink">Convites</h2>
        {!formOpen && (
          <button
            type="button"
            onClick={() => {
              setFormOpen(true);
              createInvite.reset();
            }}
            className="h-10 rounded-full bg-primary px-5 text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-primary-hover"
          >
            Novo convite
          </button>
        )}
      </div>

      {formOpen && (
        <form onSubmit={handleCreate} className="mt-4 rounded-lg border border-border bg-canvas p-4">
          <div className="grid gap-3 sm:grid-cols-[1fr_140px]">
            <div>
              <label htmlFor="invite-email" className="mb-1 block text-[13px] text-muted">
                E-mail (opcional — restringe quem pode usar)
              </label>
              <input
                id="invite-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="pessoa@exemplo.com"
                className="h-11 w-full rounded-lg border border-field bg-surface px-3 text-[15px] text-ink transition-colors focus:border-primary focus:outline-none"
              />
            </div>
            <div>
              <label htmlFor="invite-days" className="mb-1 block text-[13px] text-muted">
                Validade (dias)
              </label>
              <input
                id="invite-days"
                type="number"
                min={1}
                max={90}
                value={days}
                onChange={(e) => setDays(e.target.value)}
                className="h-11 w-full rounded-lg border border-field bg-surface px-3 text-[15px] text-ink transition-colors focus:border-primary focus:outline-none"
              />
            </div>
          </div>

          {createInvite.isError && (
            <p className="mt-3 text-sm text-expense">{errorMessage(createInvite.error)}</p>
          )}

          <div className="mt-4 flex items-center gap-2">
            <button
              type="submit"
              disabled={createInvite.isPending}
              className="flex h-10 items-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-primary-hover disabled:opacity-60"
            >
              {createInvite.isPending && <Loader2 className="size-4 animate-spin" />}
              Criar
            </button>
            <button
              type="button"
              onClick={() => {
                setFormOpen(false);
                createInvite.reset();
              }}
              className="h-10 rounded-full px-5 text-sm font-semibold uppercase tracking-wide text-muted transition-colors hover:bg-canvas hover:text-ink"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      <div className="mt-4">
        {invitesQuery.isLoading ? (
          <div className="space-y-3" aria-hidden>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-12 rounded-lg" />
            ))}
          </div>
        ) : invitesQuery.isError ? (
          <p className="py-6 text-center text-sm text-expense">{errorMessage(invitesQuery.error)}</p>
        ) : !invites || invites.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted">
            Nenhum convite criado ainda. Gere um código para convidar alguém.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {invites.map((invite) => (
              <InviteRow
                key={invite.id}
                invite={invite}
                onDelete={setConfirmTarget}
                deleting={deleteInvite.isPending && deleteInvite.variables === invite.id}
              />
            ))}
          </ul>
        )}
        {deleteInvite.isError && (
          <p className="mt-2 text-sm text-expense">{errorMessage(deleteInvite.error)}</p>
        )}
      </div>

      <ConfirmDialog
        open={confirmTarget !== null}
        title="Excluir convite?"
        message={
          confirmTarget
            ? `O código ${confirmTarget.code} deixará de funcionar. Esta ação não pode ser desfeita.`
            : ''
        }
        confirmLabel="Excluir"
        loading={deleteInvite.isPending}
        onConfirm={() => confirmTarget && deleteInvite.mutate(confirmTarget.id)}
        onCancel={() => setConfirmTarget(null)}
      />
    </Card>
  );
}

// --- Aba: Conta ------------------------------------------------------------

function AccountTab({ user }: { user: PublicUser | null }) {
  return (
    <div className="space-y-6">
      <Card>
        <h2 className="text-lg font-semibold text-ink">Minha conta</h2>
        <dl className="mt-4 space-y-3">
          <div>
            <dt className="text-[13px] text-muted">Nome</dt>
            <dd className="text-[15px] font-medium text-ink">{user?.name ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-[13px] text-muted">E-mail</dt>
            <dd className="text-[15px] font-medium text-ink">{user?.email ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-[13px] text-muted">Papel</dt>
            <dd className="text-[15px] font-medium text-ink">{user ? ROLE_LABEL[user.role] : '—'}</dd>
          </div>
        </dl>
      </Card>

      {user?.role === 'admin' && <InvitesCard />}
    </div>
  );
}

// --- Aba: Aparência --------------------------------------------------------

const THEME_OPTIONS: { key: ThemePref; label: string; icon: typeof Sun }[] = [
  { key: 'light', label: 'Claro', icon: Sun },
  { key: 'dark', label: 'Escuro', icon: Moon },
  { key: 'system', label: 'Sistema', icon: Monitor },
];

function ThemeSelector() {
  const preference = useThemeStore((s) => s.preference);
  const setPreference = useThemeStore((s) => s.setPreference);

  return (
    <div>
      <p className="text-[15px] text-ink">Tema</p>
      <p className="mt-0.5 text-sm text-muted">
        Sua preferência fica salva na conta e vale em qualquer dispositivo.
      </p>
      <div
        role="radiogroup"
        aria-label="Tema do aplicativo"
        className="mt-3 grid max-w-md grid-cols-3 gap-2"
      >
        {THEME_OPTIONS.map(({ key, label, icon: Icon }) => {
          const active = preference === key;
          return (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setPreference(key)}
              className={cn(
                'flex flex-col items-center gap-1.5 rounded-lg border p-3 text-sm font-medium transition-colors',
                active
                  ? 'border-primary bg-primary-weak text-primary'
                  : 'border-field text-muted hover:border-muted hover:text-ink',
              )}
            >
              <Icon className="size-5" />
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AppearanceTab() {
  const hidden = usePrivacyStore((s) => s.hidden);
  const togglePrivacy = usePrivacyStore((s) => s.toggle);

  return (
    <Card>
      <h2 className="text-lg font-semibold text-ink">Aparência</h2>

      <div className="mt-4 divide-y divide-border">
        <div className="pb-5">
          <ThemeSelector />
        </div>

        <div className="pt-5">
          <Toggle checked={hidden} onChange={() => togglePrivacy()} label="Ocultar valores" />
          <p className="mt-1 text-sm text-muted">
            Esconde todos os valores monetários do app (ícone de olho no cabeçalho).
          </p>
        </div>
      </div>
    </Card>
  );
}

// --- Aba: Segurança --------------------------------------------------------

function PasswordField({
  id,
  label,
  value,
  onChange,
  autoComplete,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-[13px] text-muted">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          className="h-11 w-full rounded-lg border border-field bg-surface pl-3 pr-11 text-[15px] text-ink transition-colors focus:border-primary focus:outline-none"
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          aria-label={show ? 'Ocultar senha' : 'Mostrar senha'}
          title={show ? 'Ocultar senha' : 'Mostrar senha'}
          className="absolute inset-y-0 right-0 grid w-11 place-items-center rounded-r-lg text-muted transition-colors hover:text-ink"
        >
          {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      </div>
    </div>
  );
}

function ChangePasswordCard() {
  const queryClient = useQueryClient();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const changePassword = useMutation({
    mutationFn: (body: ChangePasswordBody) => apiPatch('/auth/password', body),
    onSuccess: () => {
      toast('Senha alterada. Os outros dispositivos foram desconectados.', 'success');
      setCurrent('');
      setNext('');
      setConfirm('');
      setLocalError(null);
      // O backend revoga as outras sessões — atualiza a lista de dispositivos.
      void queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (changePassword.isPending) return;

    if (!current) return setLocalError('Informe a senha atual.');
    if (next.length < 8) return setLocalError('A nova senha precisa de pelo menos 8 caracteres.');
    if (next !== confirm) return setLocalError('A confirmação não confere com a nova senha.');
    if (next === current) return setLocalError('A nova senha deve ser diferente da atual.');

    setLocalError(null);
    changePassword.mutate({ currentPassword: current, newPassword: next });
  };

  const error = localError ?? (changePassword.isError ? errorMessage(changePassword.error) : null);

  return (
    <Card>
      <div className="flex items-center gap-2">
        <span className="grid size-8 place-items-center rounded-full bg-primary-weak text-primary">
          <KeyRound className="size-4" />
        </span>
        <h2 className="text-lg font-semibold text-ink">Mudar minha senha</h2>
      </div>
      <p className="mt-1 text-sm text-muted">
        Ao trocar a senha, os outros dispositivos conectados são desconectados por segurança.
      </p>

      <form onSubmit={handleSubmit} className="mt-4 max-w-md space-y-3">
        <PasswordField
          id="pw-current"
          label="Senha atual"
          value={current}
          onChange={setCurrent}
          autoComplete="current-password"
        />
        <PasswordField
          id="pw-new"
          label="Nova senha"
          value={next}
          onChange={setNext}
          autoComplete="new-password"
        />
        <PasswordField
          id="pw-confirm"
          label="Confirmar nova senha"
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
        />

        {error && (
          <p className="text-sm text-expense" role="alert">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={changePassword.isPending}
          className="flex h-10 items-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-primary-hover disabled:opacity-60"
        >
          {changePassword.isPending && <Loader2 className="size-4 animate-spin" />}
          Salvar nova senha
        </button>
      </form>
    </Card>
  );
}

function SessionRow({
  session,
  onDisconnect,
  disconnecting,
}: {
  session: SessionInfo;
  onDisconnect: (session: SessionInfo) => void;
  disconnecting: boolean;
}) {
  const ua = parseUserAgent(session.userAgent);
  const DeviceIcon = ua.isMobile ? Smartphone : Monitor;
  const lastAccess = dateBR(session.lastUsedAt ?? session.createdAt);

  return (
    <li className="flex items-center gap-3 py-3">
      <span className="grid size-10 shrink-0 place-items-center rounded-full bg-canvas text-muted">
        <DeviceIcon className="size-5" />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate font-medium text-ink">{ua.summary}</p>
          {session.isCurrent && (
            <span className="shrink-0 rounded-full bg-primary-weak px-2 py-0.5 text-[11px] font-semibold text-primary">
              Este dispositivo
            </span>
          )}
        </div>
        <p className="truncate text-[13px] text-muted">
          Último acesso {lastAccess}
          {session.ip ? ` · ${session.ip}` : ''}
        </p>
      </div>

      {!session.isCurrent && (
        <button
          type="button"
          onClick={() => onDisconnect(session)}
          disabled={disconnecting}
          className="shrink-0 rounded-full px-3 py-1.5 text-sm font-semibold text-muted transition-colors hover:bg-expense-weak hover:text-expense disabled:opacity-50"
        >
          Desconectar
        </button>
      )}
    </li>
  );
}

function SessionsCard() {
  const queryClient = useQueryClient();
  const [confirmTarget, setConfirmTarget] = useState<SessionInfo | null>(null);

  const sessionsQuery = useQuery({
    queryKey: ['sessions'],
    queryFn: () => apiGet<SessionsResponse>('/auth/sessions'),
  });

  const disconnect = useMutation({
    mutationFn: (familyId: string) => apiDelete(`/auth/sessions/${familyId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sessions'] });
      toast('Dispositivo desconectado.', 'success');
      setConfirmTarget(null);
    },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const sessions = sessionsQuery.data?.sessions;

  return (
    <Card>
      <div className="flex items-center gap-2">
        <span className="grid size-8 place-items-center rounded-full bg-primary-weak text-primary">
          <ShieldCheck className="size-4" />
        </span>
        <h2 className="text-lg font-semibold text-ink">Dispositivos conectados</h2>
      </div>
      <p className="mt-1 text-sm text-muted">
        Sessões ativas na sua conta. Desconecte qualquer aparelho que você não reconheça.
      </p>

      <div className="mt-4">
        {sessionsQuery.isLoading ? (
          <div className="space-y-3" aria-hidden>
            {[0, 1].map((i) => (
              <Skeleton key={i} className="h-14 rounded-lg" />
            ))}
          </div>
        ) : sessionsQuery.isError ? (
          <p className="py-6 text-center text-sm text-expense">{errorMessage(sessionsQuery.error)}</p>
        ) : !sessions || sessions.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted">Nenhuma sessão ativa encontrada.</p>
        ) : (
          <ul className="divide-y divide-border">
            {sessions.map((session) => (
              <SessionRow
                key={session.familyId}
                session={session}
                onDisconnect={setConfirmTarget}
                disconnecting={
                  disconnect.isPending && disconnect.variables === session.familyId
                }
              />
            ))}
          </ul>
        )}
      </div>

      <ConfirmDialog
        open={confirmTarget !== null}
        title="Desconectar dispositivo?"
        message={
          confirmTarget
            ? `A sessão em ${parseUserAgent(confirmTarget.userAgent).summary} será encerrada e precisará entrar novamente.`
            : ''
        }
        confirmLabel="Desconectar"
        loading={disconnect.isPending}
        onConfirm={() => confirmTarget && disconnect.mutate(confirmTarget.familyId)}
        onCancel={() => setConfirmTarget(null)}
      />
    </Card>
  );
}

interface BackupResult {
  path: string;
  sizeBytes: number;
}

function BackupCard() {
  const backup = useMutation({
    mutationFn: () => apiPost<BackupResult>('/admin/backup'),
    onSuccess: (res) => toast(`Backup gerado (${formatBytes(res.sizeBytes)}).`, 'success'),
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  return (
    <Card>
      <div className="flex items-center gap-2">
        <span className="grid size-8 place-items-center rounded-full bg-primary-weak text-primary">
          <DatabaseBackup className="size-4" />
        </span>
        <h2 className="text-lg font-semibold text-ink">Backup do banco</h2>
      </div>
      <p className="mt-1 text-sm text-muted">
        Gera uma cópia consistente do banco no servidor (via Backup API do SQLite).
      </p>

      <button
        type="button"
        onClick={() => backup.mutate()}
        disabled={backup.isPending}
        className="mt-4 flex min-h-11 items-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-primary-hover disabled:opacity-60"
      >
        {backup.isPending && <Loader2 className="size-4 animate-spin" />}
        Fazer backup do banco
      </button>

      {backup.isSuccess && (
        <p className="mt-3 truncate text-[13px] text-muted">
          Arquivo: <span className="font-mono text-ink">{backup.data.path}</span>
        </p>
      )}
    </Card>
  );
}

function NotificationsTab() {
  const queryClient = useQueryClient();
  const profileQuery = useQuery({
    queryKey: ['financial-profile'],
    queryFn: () => apiGet<FinancialProfileContext>('/financial-profile'),
  });
  const [enabled, setEnabled] = useState(false);
  const [channel, setChannel] = useState<UpdateNotificationPreferencesBody['channel']>('none');
  const [ntfyTopic, setNtfyTopic] = useState('');
  const [telegramChatId, setTelegramChatId] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    const preferences = profileQuery.data?.notificationPreferences;
    if (!preferences) return;
    setEnabled(preferences.fortnightlyReportEnabled);
    setChannel(preferences.channel);
    setNtfyTopic(preferences.ntfyTopic ?? '');
    setTelegramChatId(preferences.telegramChatId ?? '');
  }, [profileQuery.data]);

  const save = useMutation({
    mutationFn: (body: UpdateNotificationPreferencesBody) =>
      apiPatch<{ notificationPreferences: NotificationPreferences }>('/financial-profile/notifications', body),
    onSuccess: (data) => {
      queryClient.setQueryData<FinancialProfileContext>(['financial-profile'], (current) =>
        current ? { ...current, notificationPreferences: data.notificationPreferences } : current,
      );
      setLocalError(null);
      toast('Preferências de notificação salvas.', 'success');
    },
    onError: (error) => setLocalError(errorMessage(error)),
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const preferences = profileQuery.data?.notificationPreferences;
    if (save.isPending) return;
    const needsNtfy = enabled && (channel === 'ntfy' || channel === 'both');
    const needsTelegram = enabled && (channel === 'telegram' || channel === 'both');
    if (enabled && channel === 'none') return setLocalError('Escolha pelo menos um canal.');
    if (needsNtfy && !ntfyTopic.trim()) return setLocalError('Informe o tópico ntfy.');
    if (needsTelegram && !telegramChatId.trim()) return setLocalError('Informe o Chat ID do Telegram.');

    setLocalError(null);
    save.mutate({
      confirmation: 'CONFIRMAR PREFERÊNCIAS',
      channel,
      quietHoursStart: preferences?.quietHoursStart ?? null,
      quietHoursEnd: preferences?.quietHoursEnd ?? null,
      spendingAlertPercent: preferences?.spendingAlertPercent ?? null,
      weeklyReportEnabled: preferences?.weeklyReportEnabled ?? false,
      monthlyReportEnabled: preferences?.monthlyReportEnabled ?? false,
      fortnightlyReportEnabled: enabled,
      ntfyTopic: ntfyTopic.trim() || null,
      telegramChatId: telegramChatId.trim() || null,
      provenance: { source: 'confirmed_decision', reference: 'settings-notifications' },
    });
  };

  return (
    <Card>
      <div className="flex items-center gap-2">
        <span className="grid size-8 place-items-center rounded-full bg-primary-weak text-primary">
          <Bell className="size-4" />
        </span>
        <h2 className="text-lg font-semibold text-ink">Resumo financeiro do Sam</h2>
      </div>
      <p className="mt-1 max-w-2xl text-sm text-muted">
        No dia 16, receba o fechamento dos primeiros 15 dias. No dia 1, receba o fechamento completo do mês anterior.
      </p>

      {profileQuery.isLoading ? (
        <Skeleton className="mt-5 h-72 rounded-lg" />
      ) : profileQuery.isError ? (
        <p className="mt-5 text-sm text-expense">{errorMessage(profileQuery.error)}</p>
      ) : (
        <form onSubmit={submit} className="mt-5 max-w-xl space-y-4">
          <label className="flex min-h-11 items-center gap-3 rounded-xl border border-border px-4 py-3 text-sm font-medium text-ink">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(event) => setEnabled(event.target.checked)}
              className="size-4 accent-primary"
            />
            Receber resumo financeiro do Sam
          </label>

          <label className="block text-sm font-medium text-ink">
            Canais do resumo
            <select
              aria-label="Canais do resumo"
              value={channel}
              onChange={(event) => setChannel(event.target.value as UpdateNotificationPreferencesBody['channel'])}
              className="mt-1.5 h-11 w-full rounded-xl border border-border bg-surface px-3 text-ink outline-none focus:border-primary"
            >
              <option value="none">Nenhum</option>
              <option value="ntfy">ntfy</option>
              <option value="telegram">Telegram</option>
              <option value="both">ntfy e Telegram</option>
            </select>
          </label>

          {(channel === 'ntfy' || channel === 'both') && (
            <label className="block text-sm font-medium text-ink">
              Tópico ntfy
              <input
                aria-label="Tópico ntfy"
                value={ntfyTopic}
                onChange={(event) => setNtfyTopic(event.target.value)}
                placeholder="minhas-financas"
                autoComplete="off"
                className="mt-1.5 h-11 w-full rounded-xl border border-border bg-surface px-3 text-ink outline-none focus:border-primary"
              />
            </label>
          )}

          {(channel === 'telegram' || channel === 'both') && (
            <label className="block text-sm font-medium text-ink">
              Chat ID do Telegram
              <input
                aria-label="Chat ID do Telegram"
                value={telegramChatId}
                onChange={(event) => setTelegramChatId(event.target.value)}
                placeholder="-1001234567890"
                inputMode="numeric"
                autoComplete="off"
                className="mt-1.5 h-11 w-full rounded-xl border border-border bg-surface px-3 text-ink outline-none focus:border-primary"
              />
            </label>
          )}

          <p className="text-xs text-muted">
            O endereço do ntfy e os tokens dos provedores são definidos pelo administrador no arquivo <code>.env</code> do servidor.
          </p>
          {localError && <p role="alert" className="text-sm text-expense">{localError}</p>}
          <button
            type="submit"
            disabled={save.isPending}
            className="flex min-h-11 items-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold uppercase tracking-wide text-white hover:bg-primary-hover disabled:opacity-60"
          >
            {save.isPending && <Loader2 className="size-4 animate-spin" />}
            Salvar notificações
          </button>
        </form>
      )}
    </Card>
  );
}

function SecurityTab({ isAdmin }: { isAdmin: boolean }) {
  return (
    <div className="space-y-6">
      <ChangePasswordCard />
      <SessionsCard />
      <ApiTokensCard />
      {isAdmin && <BackupCard />}
    </div>
  );
}

// --- Abas (pill segmentado) ------------------------------------------------

type TabKey = 'conta' | 'aparencia' | 'notificacoes' | 'open-finance' | 'seguranca' | 'usuarios' | 'suporte';
type Tab = { key: TabKey; label: string };

function SettingsTabs({
  tabs,
  value,
  onChange,
}: {
  tabs: Tab[];
  value: TabKey;
  onChange: (v: TabKey) => void;
}) {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const dir = e.key === 'ArrowRight' ? 1 : -1;
    const nextIndex = (index + dir + tabs.length) % tabs.length;
    const nextTab = tabs[nextIndex]!;
    onChange(nextTab.key);
    tabRefs.current[nextIndex]?.focus();
  };

  return (
    <div className="w-full min-w-0 overflow-x-auto overscroll-x-contain pb-1">
      <div
        role="tablist"
        aria-label="Seções das configurações"
        className="inline-flex min-w-max items-center gap-1 rounded-full bg-canvas p-1"
      >
        {tabs.map(({ key, label }, index) => {
          const active = value === key;
          return (
            <button
              key={key}
              ref={(el) => {
                tabRefs.current[index] = el;
              }}
              type="button"
              role="tab"
              id={`settings-tab-${key}`}
              aria-selected={active}
              aria-controls={`settings-panel-${key}`}
              tabIndex={active ? 0 : -1}
              onClick={() => onChange(key)}
              onKeyDown={(e) => onKeyDown(e, index)}
              className={cn(
                'min-h-11 rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
                active ? 'bg-surface text-primary shadow-card' : 'text-muted hover:text-ink',
              )}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Configurações (M7): abas Conta · Aparência · Segurança. */
export function SettingsPage() {
  const user = useAuthStore((s) => s.user);
  const [tab, setTab] = useState<TabKey>('conta');
  const isAdmin = user?.role === 'admin';

  const tabs: Tab[] = [
    { key: 'conta', label: 'Conta' },
    { key: 'aparencia', label: 'Aparência' },
    { key: 'notificacoes', label: 'Notificações' },
    { key: 'open-finance', label: 'Open Finance' },
    { key: 'seguranca', label: 'Segurança' },
    ...(isAdmin ? [{ key: 'usuarios', label: 'Usuários' } as Tab, { key: 'suporte', label: 'Suporte' } as Tab] : []),
  ];

  return (
    <section className="space-y-6">
      <PageHeader
        title="Configurações"
        subtitle="Conta, aparência, segurança e preferências do Lira."
      />

      <SettingsTabs tabs={tabs} value={tab} onChange={setTab} />

      <div
        role="tabpanel"
        id={`settings-panel-${tab}`}
        aria-labelledby={`settings-tab-${tab}`}
        tabIndex={0}
        className="focus:outline-none"
      >
        {tab === 'conta' && <AccountTab user={user} />}
        {tab === 'aparencia' && <AppearanceTab />}
        {tab === 'notificacoes' && <NotificationsTab />}
        {tab === 'open-finance' && <OpenFinanceSettingsTab />}
        {tab === 'seguranca' && <SecurityTab isAdmin={isAdmin} />}
        {tab === 'usuarios' && isAdmin && <UsersAdmin />}
        {tab === 'suporte' && isAdmin && <SupportAdmin />}
      </div>
    </section>
  );
}
